// ============================================================================
// LE CODE COMMERÇANT NEPTING D'UNE CAISSE — sa saisie, depuis la fiche du magasin et depuis l'assistant de création.
//
// ⚠️ LA RÈGLE DE SAISIE EST CELLE DU CONTRAT (`neptingTerminalProblems`) : la caisse et le back-office liront la valeur
//    par la même règle. L'écran la montre AVANT l'envoi ; le serveur la rejuge, l'écran n'est jamais la seule garde.
// ⚠️ LES DEUX CHAMPS SONT UN SEUL COMPOSANT (`NeptingTerminalFields`), partagé par la fiche et par l'assistant : deux
//    copies de la même saisie finiraient par accepter deux choses différentes.
// ⚠️ « RETIRER » EST UN GESTE À PART ENTIÈRE : sans code, la caisse ouvre sa session sur la configuration faite à la main
//    dans l'application Nepting du terminal — la fenêtre le dit avant qu'on le fasse.
// ============================================================================
import { useId, useState, type CSSProperties } from 'react';
import { neptingTerminalProblems, normalizeMerchantCode, NEPTING_URL_MAX, type NeptingTerminal } from '@orgazone/sync-contract';
import { alert, codeCaisse, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { api } from '../api';

/**
 * Ce qui empêche d'enregistrer la saisie — vide si rien, et vide aussi quand les deux champs sont vides (rien à poser).
 * ⚠️ UNE ADRESSE SANS CODE EST REFUSÉE, comme la route la refuse : partie seule, elle ne serait posée nulle part, et
 *    l'opérateur croirait avoir réglé le serveur de la caisse.
 */
export function neptingEntryProblems(code: string, url: string): string[] {
  if (!code.trim()) return url.trim() ? ['une adresse de serveur sans code commerçant ne sert à rien : renseignez le code, ou videz l’adresse'] : [];
  return neptingTerminalProblems({ merchantCode: code, webServiceUrl: url.trim() || null });
}

const FIELD_LABEL: CSSProperties = { display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', margin: '4px 0 6px' };
const FIELD_INPUT: CSSProperties = { width: '100%', height: 36, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', fontFamily: 'var(--font-mono)' };

/**
 * Le code commerçant et l'adresse du serveur Nepting d'une caisse, avec ce que la règle refuse dessous. Les styles
 * s'adaptent à l'écran qui les accueille ; les libellés, les marque-places et la règle ne changent jamais.
 */
export function NeptingTerminalFields({ code, url, onCode, onUrl, labelStyle = FIELD_LABEL, inputStyle = FIELD_INPUT }: {
  code: string;
  url: string;
  onCode: (v: string) => void;
  onUrl: (v: string) => void;
  labelStyle?: CSSProperties;
  inputStyle?: CSSProperties;
}) {
  const codeId = useId();
  const urlId = useId();
  const problems = neptingEntryProblems(code, url);
  return (
    <>
      <label htmlFor={codeId} style={labelStyle}>Code commerçant Nepting</label>
      <input id={codeId} value={code} onChange={(e) => onCode(e.target.value)} inputMode="numeric" autoComplete="off"
        placeholder="Fourni par Nepting — des chiffres seulement" style={inputStyle} />

      <label htmlFor={urlId} style={{ ...labelStyle, marginTop: 12 }}>
        Adresse du serveur Nepting <span style={{ color: 'var(--ink500)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(facultative)</span>
      </label>
      <input id={urlId} value={url} onChange={(e) => onUrl(e.target.value)} autoComplete="off" maxLength={NEPTING_URL_MAX}
        placeholder="https://… — vide : celle que l’application Nepting connaît" style={inputStyle} />

      {problems.length > 0 && (
        <ul role="alert" style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 'var(--t-note)', color: 'var(--da-fg)', lineHeight: 1.5 }}>
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {code.trim() && problems.length === 0 && normalizeMerchantCode(code) !== code.trim() && (
        <div style={{ marginTop: 8, fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>
          Enregistré sans espaces : <span className="mono">{normalizeMerchantCode(code)}</span>
        </div>
      )}
    </>
  );
}

export function NeptingTerminalModal({ storeId, storeName, registerId, deviceLabel, current, onClose, onSaved }: {
  storeId: string;
  storeName: string;
  /** Le n° de caisse scellé (« 01 »). */
  registerId: string;
  /** L'appareil affecté à cette caisse, pour savoir de quel comptoir on parle. */
  deviceLabel: string | null;
  current: NeptingTerminal | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const titleId = useId();
  const [code, setCode] = useState(current?.merchantCode ?? '');
  const [url, setUrl] = useState(current?.webServiceUrl ?? '');
  const [busy, setBusy] = useState(false);
  // ⚠️ Échap ferme et le focus revient au déclencheur ; refusé pendant l'envoi, comme le clic sur le fond.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });
  const ready = !!code.trim() && neptingEntryProblems(code, url).length === 0 && !busy;

  /** Envoie le code (ou son retrait) ; la fenêtre se ferme seulement sur un enregistrement, avec ce qui en a été fait. */
  const send = async (merchantCode: string, webServiceUrl: string | null) => {
    setBusy(true);
    try {
      const r = await api.setStoreNeptingTerminal(storeId, registerId, { merchantCode, webServiceUrl });
      const what = merchantCode ? `Code commerçant de la caisse ${codeCaisse(registerId)} enregistré` : `Code commerçant de la caisse ${codeCaisse(registerId)} retiré`;
      if (r.descend.ok) showToast(`${what} — ${r.descend.note}.`);
      else await alert({ title: `${what}, pas encore transmis`, message: `Il est écrit dans la base du magasin, mais il n’a pas atteint les caisses : ${r.descend.note}. Re-déclarez le magasin au back-office (section Données) pour le transmettre.`, tone: 'err' });
      onSaved();
      onClose();
    } catch (e) {
      await alert({ title: 'Code commerçant inchangé', message: (e as Error).message, tone: 'err' });
      setBusy(false);
    }
  };

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        style={{ width: 'min(94vw, 500px)' }} onClick={(e) => e.stopPropagation()}>
        <h3 id={titleId} style={{ marginBottom: 4 }}>Paiement par carte — caisse {codeCaisse(registerId)}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 12, lineHeight: 1.5 }}>
          {deviceLabel ? `${deviceLabel} · ` : ''}« {storeName} ». La caisse envoie ce code à l’application Nepting à l’ouverture de sa session de paiement — au démarrage, une fois par jour et après chaque changement. Le back-office du client le voit sans pouvoir le changer.
        </div>

        <NeptingTerminalFields code={code} url={url} onCode={setCode} onUrl={setUrl} />

        <div className="modalfoot">
          {current && (
            <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 14px', marginRight: 'auto' }} disabled={busy}
              title="Sans code, la caisse ouvre sa session sur la configuration de l’application Nepting du terminal."
              onClick={() => void send('', null)}>
              Retirer le code
            </button>
          )}
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 16px' }} onClick={onClose} disabled={busy}>Annuler</button>
          <button className="btn btn-cta" style={{ width: 'auto', margin: 0, padding: '9px 18px' }} disabled={!ready}
            onClick={() => void send(normalizeMerchantCode(code), url.trim() || null)}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
